/**
 * useWebSocketTracking - Hook React pour le tracking temps réel via WebSocket
 * 
 * Usage:
 * const { position, status, isConnected, error } = useWebSocketTracking(operationId);
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import { buildAuthenticatedWsUrl } from '../utils/wsTicket';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SEC-003 — L'auth WS passe par un ticket court-vie obtenu via
// /api/auth/ws-ticket (le cookie HttpOnly baobab_session n'étant pas
// envoyé par tous les browsers sur les WS cross-origin).
// `buildAuthenticatedWsUrl` est async (HTTP roundtrip pour le ticket).
const getWebSocketUrl = (path) => buildAuthenticatedWsUrl(path);

/**
 * Hook pour suivre une opération en temps réel (côté CLIENT)
 */
export const useWebSocketTracking = (operationId) => {
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(async () => {
    if (!operationId) return;

    try {
      const wsUrl = await getWebSocketUrl(`/ws/tracking/${operationId}`);
      if (!wsUrl) {
        // Pas authentifié — ne pas tenter de se connecter
        setError('Authentification requise');
        return;
      }
      logger.debug('Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        logger.debug('WebSocket connected');
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        
        // Demander la dernière position connue
        ws.send(JSON.stringify({ type: 'request_position' }));
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          switch (message.type) {
            case 'position_update':
              setPosition(message.data);
              setLastUpdate(new Date());
              break;
              
            case 'status_update':
              setStatus(message.data);
              setLastUpdate(new Date());
              break;
              
            case 'no_position':
              logger.debug('No position available yet');
              break;
              
            case 'pong':
              // Heartbeat response
              break;
              
            default:
              logger.debug('Unknown message type:', message.type);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
        setError('Erreur de connexion WebSocket');
      };
      
      ws.onclose = (e) => {
        logger.debug('WebSocket closed:', e.code, e.reason);
        setIsConnected(false);
        
        // Tentative de reconnexion automatique
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          logger.debug(`Reconnecting in ${delay}ms...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else {
          setError('Connexion perdue. Veuillez rafraîchir la page.');
        }
      };
      
    } catch (e) {
      console.error('Error creating WebSocket:', e);
      setError('Impossible de créer la connexion WebSocket');
    }
  }, [operationId]);

  // Heartbeat pour maintenir la connexion
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;
    
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping toutes les 30 secondes
    
    return () => clearInterval(heartbeat);
  }, [isConnected]);

  // Connexion initiale
  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  // Fonction pour demander une mise à jour manuelle
  const requestUpdate = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_position' }));
    }
  }, []);

  return {
    position,
    status,
    isConnected,
    error,
    lastUpdate,
    requestUpdate
  };
};

/**
 * Hook pour les ADMINS - voir toutes les opérations
 */
export const useAdminWebSocketTracking = () => {
  const [positions, setPositions] = useState([]);
  const [stats, setStats] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);

  const connect = useCallback(async () => {
    try {
      const wsUrl = await getWebSocketUrl('/ws/admin/tracking');
      if (!wsUrl) {
        setError('Authentification requise');
        return;
      }
      logger.debug('Admin connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        logger.debug('Admin WebSocket connected');
        setIsConnected(true);
        setError(null);
        
        // Demander toutes les positions
        ws.send(JSON.stringify({ type: 'get_all_positions' }));
        ws.send(JSON.stringify({ type: 'get_stats' }));
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          switch (message.type) {
            case 'position_update':
              // Mise à jour d'une position individuelle
              setPositions(prev => {
                const existing = prev.findIndex(p => p.operation_id === message.data.operation_id);
                if (existing >= 0) {
                  const updated = [...prev];
                  updated[existing] = message.data;
                  return updated;
                }
                return [...prev, message.data];
              });
              break;
              
            case 'all_positions':
              setPositions(message.data);
              break;
              
            case 'stats':
              setStats(message.data);
              break;
              
            case 'status_update':
              // Notification de changement de statut
              logger.debug('Status update:', message.data);
              break;
              
            default:
              break;
          }
        } catch (e) {
          console.error('Error parsing admin WebSocket message:', e);
        }
      };
      
      ws.onerror = (e) => {
        console.error('Admin WebSocket error:', e);
        setError('Erreur de connexion admin');
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        // Reconnexion automatique après 5 secondes
        setTimeout(connect, 5000);
      };
      
    } catch (e) {
      console.error('Error creating admin WebSocket:', e);
      setError('Impossible de créer la connexion admin');
    }
  }, []);

  useEffect(() => {
    connect();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const requestStats = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'get_stats' }));
    }
  }, []);

  const requestAllPositions = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'get_all_positions' }));
    }
  }, []);

  return {
    positions,
    stats,
    isConnected,
    error,
    requestStats,
    requestAllPositions
  };
};

export default useWebSocketTracking;
